import { useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import {
  paths,
  PLAN_FEATURE_GROUPS,
  PLAN_FEATURES,
  PLAN_TIERS,
  PLAN_TIER_DEFAULTS,
  tierFeatureFlags,
  tierOf,
  type Plan,
  type PlanTier,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { RingRatesCard } from "./RingAdminCards";
import { ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";

type Row = Plan & { id: string };

// The plan switches come from the feature catalog (shared-types/planFeatures.ts).
const FEATURES = PLAN_FEATURES.filter((f) => f.key).map((f) => ({ key: f.key!, label: f.label.split(":")[0]!, full: f }));
const SWITCH_COUNT = FEATURES.length;

const emptyForm = {
  id: null as string | null,
  name: "",
  monthlyPrice: "",
  seats: "",
  showOnPricingPage: false,
  bullets: "",
  features: tierFeatureFlags("apprentice") as Record<string, boolean>,
};

export function AdminPricing() {
  const [plans, setPlans] = useState<Row[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"active" | "archived">("active");
  const view = useListView(plans, {
    text: (p) => [p.name, PLAN_TIER_DEFAULTS[tierOf(p.features)].name, ...Object.entries(p.features).filter(([, v]) => v).map(([k]) => FEATURES.find((f) => f.key === k)?.label ?? k)],
    sorts: {
      order: (a, b) => a.order - b.order,
      name: (a, b) => a.name.localeCompare(b.name),
      price: (a, b) => a.monthlyPriceCents - b.monthlyPriceCents,
    },
    defaultSort: "price",
    filter: (p) => (status === "archived" ? !!p.archivedAt : !p.archivedAt),
  });

  useEffect(() => {
    const q = query(collection(db, paths.plans()), orderBy("order"));
    return onSnapshot(q, (snap) => {
      setPlans(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Plan) })));
    });
  }, []);

  function edit(p: Row) {
    setForm({
      id: p.id,
      name: p.name,
      monthlyPrice: (p.monthlyPriceCents / 100).toFixed(2),
      seats: p.limits.maxUserSeats?.toString() ?? "",
      showOnPricingPage: p.showOnPricingPage,
      bullets: (p.bullets ?? []).join("\n"),
      features: { ...p.features },
    });
    setShowForm(true);
  }

  // "Start from": a tier's name (if blank or another tier's), users and switches.
  function startFrom(tier: PlanTier) {
    const d = PLAN_TIER_DEFAULTS[tier];
    const tierNames = PLAN_TIERS.map((t) => PLAN_TIER_DEFAULTS[t].name);
    setForm((f) => ({
      ...f,
      name: !f.name.trim() || tierNames.includes(f.name.trim()) ? d.name : f.name,
      seats: d.seats === null ? "" : String(d.seats),
      features: tierFeatureFlags(tier),
    }));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await call(
        "upsertPlan",
        {
          id: form.id,
          name: form.name,
          monthlyPriceCents: Math.round(parseFloat(form.monthlyPrice || "0") * 100),
          showOnPricingPage: form.showOnPricingPage,
          bullets: form.bullets
            .split("\n")
            .map((b) => b.trim())
            .filter(Boolean)
            .slice(0, 12),
          order: form.id ? plans.find((p) => p.id === form.id)?.order ?? 0 : plans.length,
          limits: {
            maxUserSeats: form.seats ? parseInt(form.seats, 10) : null,
            maxJobsPerMonth: null,
            locationsIncluded: 1,
          },
          // Every switch explicit, so a plan never inherits a switch added later by accident.
          features: Object.fromEntries(FEATURES.map((f) => [f.key, form.features[f.key] === true])),
        },
        { mutation: false },
      );
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Pricing & Plans</h1>
        <Button
          onClick={() => {
            setForm(emptyForm);
            setShowForm((v) => !v);
          }}
        >
          {showForm ? "Cancel" : "New plan"}
        </Button>
      </div>
      <p className="text-xs text-steel">
        Public pricing, no sales-call gate. Stripe product sync attaches when keys are configured.
      </p>

      <RingRatesCard />

      {showForm ? (
        <Card>
          <CardTitle>{form.id ? "Edit plan" : "New plan"}</CardTitle>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>$ / month</Label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monthlyPrice}
                  onChange={(e) => setForm({ ...form, monthlyPrice: e.target.value })}
                />
              </div>
              <div>
                <Label>Max seats (blank = unlimited)</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.seats}
                  onChange={(e) => setForm({ ...form, seats: e.target.value })}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-cream/60 p-2 text-sm">
              <span className="text-xs font-semibold text-steel">Start from</span>
              {PLAN_TIERS.map((t) => (
                <Button key={t} type="button" variant="ghost" className="border border-steel/30 px-3 py-1 text-xs" onClick={() => startFrom(t)}>
                  {PLAN_TIER_DEFAULTS[t].name}
                </Button>
              ))}
              <span className="text-[11px] text-steel">fills the name, users and switches; change anything after.</span>
            </div>
            <div>
              <Label>What this plan includes ({FEATURES.filter((f) => form.features[f.key]).length} of {SWITCH_COUNT} switches on)</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                {PLAN_FEATURE_GROUPS.map((g) => {
                  const items = PLAN_FEATURES.filter((f) => f.group === g);
                  return (
                    <div key={g} className="rounded-lg border border-steel/15 p-2">
                      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-steel">{g}</div>
                      {items.map((f) =>
                        f.key ? (
                          <label key={f.key} className="flex items-start gap-2 py-0.5 text-sm text-ink">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={form.features[f.key] === true}
                              onChange={(e) => setForm({ ...form, features: { ...form.features, [f.key!]: e.target.checked } })}
                            />
                            <span>
                              {f.label}
                              {f.soon ? <span className="ml-1 text-[11px] text-steel">(coming)</span> : null}
                              <span className="ml-1 text-[11px] text-steel">· {PLAN_TIER_DEFAULTS[f.tier].name}</span>
                            </span>
                          </label>
                        ) : (
                          <div key={f.label} className="flex items-start gap-2 py-0.5 text-sm text-steel">
                            <span className="mt-0.5 w-3.5 text-center text-success">✓</span>
                            <span>{f.label}</span>
                          </div>
                        ),
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-steel">Ticked items are switches; ✓ items come with every plan. The pricing cards list all of it.</p>
            </div>
            <div>
              <Label>Highlights at the top of the card (optional, one per line)</Label>
              <textarea
                className="w-full rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink focus:border-brandorange focus:outline-none focus:ring-1 focus:ring-brandorange"
                rows={3}
                placeholder={"Unlimited jobs and customers\nSetup help included"}
                value={form.bullets}
                onChange={(e) => setForm({ ...form, bullets: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={form.showOnPricingPage}
                onChange={(e) => setForm({ ...form, showOnPricingPage: e.target.checked })}
              />
              Show on public pricing page
            </label>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
            <Button type="submit" busy={busy}>
              Save plan
            </Button>
          </form>
        </Card>
      ) : null}

      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search plans or features…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as "active" | "archived")}>
            <option value="active">Active ({plans.filter((p) => !p.archivedAt).length})</option>
            <option value="archived">Archived ({plans.filter((p) => p.archivedAt).length})</option>
          </Select>
        </div>
        <div className="hidden grid-cols-[minmax(0,2fr)_100px_90px_minmax(0,2fr)_150px] items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid">
          <SortHeader label="Plan" k="name" view={view} />
          <SortHeader label="Price" k="price" view={view} />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">Seats</span>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">Features</span>
          <span />
        </div>
        {view.page.map((p) => (
          <div key={p.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-steel/10 px-4 py-2 text-sm last:border-0 sm:grid-cols-[minmax(0,2fr)_100px_90px_minmax(0,2fr)_150px]">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 font-medium text-ink">
                {p.name}
                {p.showOnPricingPage ? <span className="rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-success">public</span> : null}
                {p.stripePriceId ? null : <span className="rounded-full bg-brandorange/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brandorange-dark" title="Not in Stripe yet: save it again">not in Stripe</span>}
              </div>
              <div className="truncate text-[11px] text-steel sm:hidden">${(p.monthlyPriceCents / 100).toFixed(0)}/mo</div>
            </div>
            <span className="hidden font-semibold text-ink sm:block">${(p.monthlyPriceCents / 100).toFixed(0)}/mo</span>
            <span className="hidden text-steel sm:block">{p.limits.maxUserSeats ?? "Unlimited"}</span>
            <span className="hidden min-w-0 text-xs text-steel sm:block">
              <span className="rounded-full bg-ink/10 px-2 py-0.5 font-semibold text-ink">{PLAN_TIER_DEFAULTS[tierOf(p.features)].name}-level</span>{" "}
              {FEATURES.filter((f) => p.features[f.key] === true).length} of {SWITCH_COUNT} switches on
            </span>
            <div className="flex shrink-0 items-center justify-end gap-2">
              {!p.archivedAt ? (
                <>
                  <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => edit(p)}>
                    Edit
                  </Button>
                  <ConfirmButton
                    className="px-2 py-1 text-xs text-danger/80"
                    confirm="Archive?"
                    confirmLabel="Archive"
                    onConfirm={() => void call("archivePlan", { id: p.id, archived: true }, { mutation: false })}
                  >
                    Archive
                  </ConfirmButton>
                </>
              ) : (
                <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void call("archivePlan", { id: p.id, archived: false }, { mutation: false })}>
                  Restore
                </Button>
              )}
            </div>
          </div>
        ))}
        {view.filtered.length === 0 ? (
          <div className={cn("px-4 py-8 text-center text-sm text-steel")}>{status === "archived" ? "No archived plans." : plans.length ? "Nothing matches." : "No plans yet — create the first one."}</div>
        ) : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="plans" />
      </Card>
    </div>
  );
}
